import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Code, AlertCircle, Send } from 'lucide-react';

import MermaidRenderer from '../../components/docs/MermaidRenderer';
import { docsService } from '../../services/docs/docsService';
import type {
  DocsProcess,
  ProcessRevision,
  MermaidNodeBinding,
} from '../../services/docs/types';

const DocsProcessEditPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [process, setProcess] = useState<DocsProcess | null>(null);
  const [revision, setRevision] = useState<ProcessRevision | null>(null);
  const [bindings, setBindings] = useState<MermaidNodeBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Editable fields
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [mermaidSource, setMermaidSource] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');
  const [titleDirty, setTitleDirty] = useState(false);
  const [mermaidDirty, setMermaidDirty] = useState(false);

  useEffect(() => {
    if (!slug) return;

    const fetchProcess = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await docsService.getProcess(slug);
        setProcess(data.process);
        setBindings(data.bindings);

        setTitle(data.process.title);
        setSummary(data.process.summary ?? '');

        // If there's a draft revision, use it; otherwise create one
        if (data.revision && data.revision.approvalStatus === 'draft') {
          setRevision(data.revision);
          setMermaidSource(data.revision.mermaidSource ?? '');
          setChangeSummary(data.revision.changeSummary ?? '');
        } else {
          // Create a new draft
          const draftRes = await docsService.createDraftRevision(
            data.process.id
          );
          setRevision(draftRes.revision);
          setMermaidSource(draftRes.revision.mermaidSource ?? '');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load process');
      } finally {
        setLoading(false);
      }
    };

    fetchProcess();
  }, [slug]);

  const handleSave = useCallback(async () => {
    if (!process) return;
    try {
      setSaving(true);
      setError(null);
      setSaveMessage(null);

      // Save title/summary if changed
      if (titleDirty) {
        const res = await docsService.updateProcess(process.id, {
          title: title.trim(),
          summary: summary.trim() || null,
        });
        const updatedProcess = res.process;
        setProcess(updatedProcess);
        setTitleDirty(false);

        // If slug changed, update the URL to match
        if (updatedProcess.slug !== slug) {
          navigate(`/docs/processes/${updatedProcess.slug}/edit`, {
            replace: true,
          });
        }
      }

      // Save mermaid source if changed
      if (mermaidDirty && revision) {
        const res = await docsService.updateDraftRevision(process.id, {
          mermaidSource,
          changeSummary: changeSummary.trim() || null,
        });
        setRevision(res.revision);
        setMermaidDirty(false);
      }

      setSaveMessage('Saved successfully');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [
    process,
    revision,
    title,
    summary,
    mermaidSource,
    changeSummary,
    titleDirty,
    mermaidDirty,
    navigate,
    slug,
  ]);

  const isDirty = titleDirty || mermaidDirty;

  const handleSubmit = useCallback(async () => {
    if (!process) return;
    try {
      if (titleDirty || mermaidDirty) await handleSave();
      await docsService.submitProcessRevision(process.id);
      navigate(`/docs/processes/${process.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    }
  }, [process, titleDirty, mermaidDirty, handleSave, navigate]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error && !process) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(`/docs/processes/${process?.slug ?? slug}`)}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to process
        </button>
        <div className="flex items-center gap-2">
          {saveMessage && (
            <span className="text-sm text-green-600">{saveMessage}</span>
          )}
          {error && (
            <span className="flex items-center text-sm text-red-600">
              <AlertCircle className="w-4 h-4 mr-1" />
              {error}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className={`flex items-center px-4 py-2 text-sm rounded-lg ${
              isDirty
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Send className="w-4 h-4 mr-1.5" />
            Submit for Review
          </button>
        </div>
      </div>

      {/* Title and summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleDirty(true);
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Summary
            </label>
            <input
              type="text"
              value={summary}
              onChange={(e) => {
                setSummary(e.target.value);
                setTitleDirty(true);
              }}
              placeholder="Brief description of this process"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Mermaid editor with tabs */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('editor')}
            className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'editor'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Code className="w-4 h-4 mr-1.5" />
            Mermaid Source
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'preview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Eye className="w-4 h-4 mr-1.5" />
            Preview
          </button>
        </div>

        <div className="p-4">
          {activeTab === 'editor' ? (
            <textarea
              value={mermaidSource}
              onChange={(e) => {
                setMermaidSource(e.target.value);
                setMermaidDirty(true);
              }}
              placeholder={`flowchart TD\n  A[Start] --> B[Step 1]\n  B --> C[Step 2]\n  C --> D[End]`}
              className="w-full h-80 font-mono text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
              spellCheck={false}
            />
          ) : (
            <div className="min-h-[20rem]">
              {mermaidSource.trim() ? (
                <MermaidRenderer source={mermaidSource} bindings={bindings} />
              ) : (
                <div className="flex items-center justify-center h-80 text-gray-400 text-sm">
                  No diagram to preview. Add Mermaid source in the editor tab.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Change summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Change Summary
        </label>
        <input
          type="text"
          value={changeSummary}
          onChange={(e) => {
            setChangeSummary(e.target.value);
            setMermaidDirty(true);
          }}
          placeholder="Describe what changed in this revision..."
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  );
};

export default DocsProcessEditPage;
